import { normalizeLocale, type AppLocale } from '../i18n/locale';

export type SupportedShopLanguage = AppLocale;

export interface LocalizedShopText {
  en: string;
  zh: string;
  ja?: string;
  jp?: string;
  es?: string;
  fr?: string;
  ru?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function createLocalizedShopText(en: string, zh: string) {
  return {
    en: en.trim(),
    zh: zh.trim(),
  };
}

function parseJsonLikeString(value: string) {
  let currentValue: unknown = value;

  for (let depth = 0; depth < 3 && typeof currentValue === 'string'; depth += 1) {
    const trimmedValue = currentValue.trim();
    if (!trimmedValue) {
      return currentValue;
    }

    const parseCandidates = [
      trimmedValue,
      trimmedValue.replace(/^"(.+)"$/s, '$1'),
      trimmedValue
        .replace(/^"(.+)"$/s, '$1')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\'),
    ];

    let hasParsed = false;

    for (const candidate of parseCandidates) {
      if (!candidate.startsWith('{') && !candidate.startsWith('[')) {
        continue;
      }

      try {
        currentValue = JSON.parse(candidate) as unknown;
        hasParsed = true;
        break;
      } catch {
        continue;
      }
    }

    if (!hasParsed) {
      return currentValue;
    }
  }

  return currentValue;
}

export function buildLocalizedShopText(en: string, zh = ''): LocalizedShopText {
  const normalized = createLocalizedShopText(en, zh);
  const fallback = normalized.en || normalized.zh;

  return {
    en: normalized.en || fallback,
    zh: normalized.zh || fallback,
  };
}

export function normalizeLocalizedShopText(value: unknown): LocalizedShopText | null {
  if (typeof value === 'string') {
    const parsedValue = parseJsonLikeString(value);
    if (parsedValue !== value) {
      return normalizeLocalizedShopText(parsedValue);
    }

    const normalized = buildLocalizedShopText(value, value);
    return normalized.en || normalized.zh ? normalized : null;
  }

  if (!isRecord(value)) {
    return null;
  }

  const englishValue = typeof value.en === 'string' ? value.en : '';
  const chineseValue = typeof value.zh === 'string' ? value.zh : '';
  const normalized = buildLocalizedShopText(englishValue, chineseValue);

  for (const language of ['ja', 'jp', 'es', 'fr', 'ru'] as const) {
    const localizedValue = typeof value[language] === 'string' ? value[language].trim() : '';
    if (localizedValue) {
      normalized[language] = localizedValue;
    }
  }

  return normalized.en || normalized.zh ? normalized : null;
}

export function getShopLanguage(language: string | undefined | null): SupportedShopLanguage {
  return normalizeLocale(language);
}

export function resolveLocalizedShopText(value: unknown, language: string | undefined | null) {
  const normalized = normalizeLocalizedShopText(value);
  if (!normalized) {
    return '';
  }

  const activeLanguage = getShopLanguage(language);
  const localizedValue = activeLanguage === 'ja'
    ? normalized.ja || normalized.jp
    : normalized[activeLanguage];

  return localizedValue || normalized.en || normalized.zh;
}

export function getLocalizedShopSearchTerms(value: unknown) {
  const normalized = normalizeLocalizedShopText(value);
  if (!normalized) {
    return [];
  }

  return Array.from(
    new Set(
      [normalized.en, normalized.zh, normalized.ja, normalized.jp, normalized.es, normalized.fr, normalized.ru]
        .map((entry) => entry?.trim().toLowerCase() ?? '')
        .filter(Boolean)
    )
  );
}
